/**
 * Message log filters: URL search params <-> API query.
 *
 * Time presets are stored as a preset name (not timestamps) so the URL and
 * the query key stay stable; the concrete `since` is computed at fetch time,
 * which keeps "last hour" a moving window under auto-refresh.
 */
import { MESSAGE_STATUSES } from '@/api/types'
import type { MessageStatus } from './types'

export const TIME_PRESETS = [
  { value: '1h', label: 'Last hour', seconds: 3600 },
  { value: '24h', label: 'Last 24 hours', seconds: 24 * 3600 },
  { value: '7d', label: 'Last 7 days', seconds: 7 * 24 * 3600 },
  { value: '30d', label: 'Last 30 days', seconds: 30 * 24 * 3600 },
] as const

export type TimePreset = (typeof TIME_PRESETS)[number]['value']
export type TestFilter = 'only' | 'live'

export interface MessageSearch {
  status?: MessageStatus[]
  event?: string
  q?: string
  /** A preset window. Ignored when `since` or `until` is set. */
  range?: TimePreset
  since?: string
  until?: string
  test?: TestFilter
}

const PRESET_VALUES = new Set<string>(TIME_PRESETS.map((p) => p.value))
const STATUS_VALUES = new Set<string>(MESSAGE_STATUSES)

function str(v: unknown): string | undefined {
  if (typeof v === 'number') return String(v)
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined
}

function isoOrUndefined(v: unknown): string | undefined {
  const s = str(v)
  if (!s) return undefined
  return Number.isNaN(new Date(s).getTime()) ? undefined : s
}

/** validateSearch for /messages. Unknown or malformed values are dropped. */
export function parseMessageSearch(raw: Record<string, unknown>): MessageSearch {
  const statusRaw = Array.isArray(raw.status)
    ? raw.status
    : typeof raw.status === 'string'
      ? raw.status.split(',')
      : []
  const status = [
    ...new Set(
      statusRaw
        .filter((s): s is string => typeof s === 'string')
        .map((s) => s.trim())
        .filter((s) => STATUS_VALUES.has(s)),
    ),
  ] as MessageStatus[]
  const range = str(raw.range)
  const test = str(raw.test)
  return {
    status: status.length ? status : undefined,
    event: str(raw.event),
    q: str(raw.q),
    range: range && PRESET_VALUES.has(range) ? (range as TimePreset) : undefined,
    since: isoOrUndefined(raw.since),
    until: isoOrUndefined(raw.until),
    test: test === 'only' || test === 'live' ? test : undefined,
  }
}

export interface MessageApiQuery {
  app?: string
  status?: string
  event?: string
  q?: string
  since?: string
  until?: string
  test?: boolean
  request_id?: string
}

/** The API query for a filter set, with presets resolved against `now`. */
export function toMessageQuery(
  f: MessageSearch & { app?: string; request_id?: string },
  now: number = Date.now(),
): MessageApiQuery {
  let since = f.since
  const until = f.until
  if (!since && !until && f.range) {
    const preset = TIME_PRESETS.find((p) => p.value === f.range)
    if (preset) since = new Date(now - preset.seconds * 1000).toISOString()
  }
  return {
    app: f.app || undefined,
    status: f.status?.length ? f.status.join(',') : undefined,
    event: f.event || undefined,
    q: f.q || undefined,
    since,
    until,
    test: f.test === 'only' ? true : f.test === 'live' ? false : undefined,
    request_id: f.request_id || undefined,
  }
}

/** True when any filter (besides app scope) narrows the log. */
export function hasActiveFilters(f: MessageSearch): boolean {
  return !!(f.status?.length || f.event || f.q || f.range || f.since || f.until || f.test)
}

/** Human summary of the time filter, for the trigger button. */
export function describeTimeFilter(f: MessageSearch): string {
  if (f.since || f.until) {
    const fmt = (iso: string) =>
      new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    if (f.since && f.until) return `${fmt(f.since)} – ${fmt(f.until)}`
    if (f.since) return `Since ${fmt(f.since)}`
    return `Before ${fmt(f.until!)}`
  }
  return TIME_PRESETS.find((p) => p.value === f.range)?.label ?? 'Any time'
}

/** `<input type="datetime-local">` value (local time, minutes) <-> ISO string. */
export function toLocalInputValue(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInputValue(v: string): string | undefined {
  if (!v) return undefined
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}
